import React from "react";
import { Employee } from "~/gql/types";
import { ImpersonationContext } from "./ImpersonationManager";
import { AuthenticationManagerContext } from "./AuthenticationManager/AuthenticationManagerDisplay";
import sign from "jwt-encode";
import {
  Authorizations,
  GetAuthorizationsAsync,
  LegacyAuthorizations,
} from "./types";
import {
  ClientAuthorizations,
  CommandAuthorization,
  MenuAuthorizations,
} from "~/gql/types2";

type MockUserProviderProps = {
  getAuthorizations?: GetAuthorizationsAsync;
  authorizations?: Partial<Authorizations>;
  impersonatedEmployee?: Employee;
  impersonatedAuths?: Authorizations;
  token?: string;
  dispatch?: React.Dispatch<any>;
  setEmployee?: React.Dispatch<Employee | null>;
};

export const defaultMockUserEmail = "person@person.com";

// Carries a real future `exp`: session teardown treats a token whose expiry
// cannot be read as expired, so a mock token without one would blank the tree.
const exampleToken = sign(
  {
    name: "Person Personson",
    email: defaultMockUserEmail,
    exp: Math.floor(Date.now() / 1000) + 60 * 60,
  },
  "secret"
);

const defaultClientAuths: ClientAuthorizations = {
  menus: [MenuAuthorizations.Impersonate],
  commands: [
    CommandAuthorization.AssignTaskRateSheetToProjectCommand,
    CommandAuthorization.AssignToProjectCommand,
    CommandAuthorization.RemoveExpenseRateSheetFromProjectCommand,
    CommandAuthorization.RemoveTaskRateSheetFromProjectCommand,
  ],
};

export const defaultAuths = {
  legacyAuth: {
    reviewWorkData: true,
    isMobileEmployee: true,
    usesWorkflowTimeEntry: true,
    reviewWorkWeeks: true,
    approveChargeAdjustments: true,
    editProjectCharges: true,
    manageRateSheets: true,
    requestCustomer: true,
    viewCustomer: true,
    viewPayrollAdminData: true,
    approveThirdPartyInvoices: true,
    createThirdPartyInvoices: true,
  },
  auth: defaultClientAuths,
} as Authorizations;

export const MockUserProvider: React.FC<MockUserProviderProps> = ({
  getAuthorizations,
  authorizations,
  token,
  impersonatedEmployee,
  impersonatedAuths,
  dispatch,
  setEmployee,
  children,
}) => {
  const allAuthorizations: Authorizations = {
    ...defaultAuths,
    auth: {
      ...defaultAuths.auth,
      ...authorizations?.auth!,
    },
    legacyAuth: {
      ...defaultAuths.legacyAuth,
      ...authorizations?.legacyAuth!,
    },
  };

  return (
    <AuthenticationManagerContext.Provider
      value={{
        jwt: token ?? exampleToken,
        authorizations: allAuthorizations,
        provider: "google",
        dispatch: dispatch ?? (() => undefined),
      }}
    >
      <ImpersonationContext.Provider
        value={{
          getAuthorizations:
            getAuthorizations ?? (() => new Promise(() => allAuthorizations)),
          employee: impersonatedEmployee ?? null,
          setEmployee: setEmployee ?? (() => undefined),
          validating: false,
          setValidating: () => undefined,
          authorizations: impersonatedAuths ?? null,
          setAuthorizations: () => undefined,
        }}
      >
        {children}
      </ImpersonationContext.Provider>
    </AuthenticationManagerContext.Provider>
  );
};

export function wrapLegacyAuths(
  auths?: Partial<LegacyAuthorizations>
): Authorizations {
  return {
    auth: {} as ClientAuthorizations,
    legacyAuth: (auths ?? defaultAuths.legacyAuth) as LegacyAuthorizations,
  };
}
